import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import api from '../../services/api';
import { X, Save, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface ExpenseFormProps {
    onClose: () => void;
    onSuccess: () => void;
    expense?: any;
}

interface ExpenseFormData {
    name: string;
    date: string;
    category: string;
    amount: string;
    currency: string;
    taxType: string;
    status: string;
    notes: string;
    startDate: string;
    endDate: string;
    customerId: string;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({ onClose, onSuccess, expense }) => {
    const { currency: userCurrency } = useAuth();
    const [customers, setCustomers] = useState<any[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [showNewCat, setShowNewCat] = useState(false);
    const [newCatName, setNewCatName] = useState('');

    useEffect(() => {
        api.get('/customers?limit=100').then(res => {
            setCustomers(res.data.customers || []);
        });
        api.get('/settings').then(res => {
            const cats: string[] = res.data.expenseCategories || [];
            if (expense?.category && !cats.some((c: string) => c.toLowerCase() === expense.category.toLowerCase())) {
                cats.push(expense.category);
            }
            setCategories(cats);
        }).catch(() => {});
    }, []);

    // Re-sync category once options are loaded
    useEffect(() => {
        if (expense?.category && categories.length > 0) {
            setValue('category', expense.category);
        }
    }, [categories]);

    // Re-sync customerId once customers are loaded
    useEffect(() => {
        if (expense?.customerId && customers.length > 0) {
            setValue('customerId', expense.customerId);
        }
    }, [customers]);

    const { register, handleSubmit, reset, control, setValue } = useForm<ExpenseFormData>({
        defaultValues: {
            name: '',
            date: new Date().toISOString().split('T')[0],
            category: '',
            amount: '',
            currency: userCurrency || 'EUR',
            taxType: 'Tax Inclusive',
            status: 'Unbilled',
            notes: '',
            startDate: '',
            endDate: '',
            customerId: ''
        }
    });

    const category = useWatch({ control, name: 'category' });
    const startDate = useWatch({ control, name: 'startDate' });
    const showDates = category === 'Software' || category === 'Hosting';
    const hideDate = showDates && !!startDate;

    useEffect(() => {
        if (expense) {
            reset({
                ...expense,
                date: new Date(expense.date).toISOString().split('T')[0],
                startDate: expense.startDate ? new Date(expense.startDate).toISOString().split('T')[0] : '',
                endDate: expense.endDate ? new Date(expense.endDate).toISOString().split('T')[0] : '',
                customerId: expense.customerId || ''
            });
        }
    }, [expense, reset]);

    const handleAddCategory = async () => {
        const trimmed = newCatName.trim();
        if (!trimmed || categories.some(c => c.toLowerCase() === trimmed.toLowerCase())) return;
        const updated = [...categories, trimmed];
        try {
            await api.put('/settings', { expenseCategories: updated });
            setCategories(updated);
            setValue('category' as any, trimmed);
            setNewCatName('');
            setShowNewCat(false);
        } catch {}
    };

    const onSubmit = async (data: any) => {
        try {
            if (expense) {
                await api.put(`/expenses/${expense.id}`, data);
            } else {
                await api.post('/expenses', data);
            }
            onSuccess();
        } catch (err) {
            console.error('Failed to save expense', err);
            alert('Failed to save expense');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl flex flex-col">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {expense ? 'Edit Expense' : 'Record Expense'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6">
                    <form id="expense-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">

                        {/* Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                            <input
                                type="text"
                                {...register('name')}
                                placeholder="e.g. Hostinger - Studio Mim"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                            {/* Date */}
                            {!hideDate && <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Date*</label>
                                <input
                                    type="date"
                                    {...register('date', { required: !hideDate ? 'Date is required' : false })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>}

                            {/* Category */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Category*</label>
                                <div className="flex gap-2">
                                    <select
                                        {...register('category', { required: 'Category is required' })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={() => setShowNewCat(v => !v)}
                                        className="p-2 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 text-gray-600"
                                        title="Add new category"
                                    >
                                        <Plus size={20} />
                                    </button>
                                </div>
                                {showNewCat && (
                                    <div className="flex gap-2 mt-2">
                                        <input
                                            type="text"
                                            value={newCatName}
                                            onChange={e => setNewCatName(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
                                            placeholder="New category name..."
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddCategory}
                                            className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                                        >
                                            Add
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                            {/* Amount */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Amount*</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    {...register('amount', { required: 'Amount is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                <select
                                    {...register('status', { required: 'Status is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="Unbilled">Unbilled</option>
                                    <option value="Billed">Billed</option>
                                </select>
                            </div>
                        </div>

                        {/* Client */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Client (who pays)</label>
                            <select
                                {...register('customerId')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="">— No client —</option>
                                {customers.map((c: any) => (
                                    <option key={c.id} value={c.id}>{c.displayName}</option>
                                ))}
                            </select>
                        </div>

                        {/* Start / End Date for Software & Hosting */}
                        {showDates && (
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                    <input
                                        type="date"
                                        {...register('startDate')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                    <input
                                        type="date"
                                        {...register('endDate')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                            <textarea
                                {...register('notes')}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                    </form>
                </div>

                <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg flex gap-3">
                    <button
                        type="submit"
                        form="expense-form"
                        className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition"
                    >
                        <Save size={18} />
                        Save
                    </button>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition text-gray-700"
                    >
                        Cancel
                    </button>
                </div>
            </div >
        </div >
    );
};

export default ExpenseForm;
